<template>
  <BittsLoading
    :is-loading="!ready"
    :with-transition="false"
    class="sharing-settings-by-partners-tab min-h-[50vh]"
  >
    <div class="sharing-settings-by-partners-tab__actions">
      <SharingSettingsByPartnersActions
        v-if="rows.length"
        :partner-options="partnerOptions"
        :owner-options="teamOwnerOptions"
        :tag-options="tagOptions"
        @search-applied="applySearch"
        @filters-applied="applyFilters"
      >
        <template #segment>
          <slot name="prepend" />
        </template>
        <template #actions>
          <slot name="actions" />
        </template>
      </SharingSettingsByPartnersActions>
      <!-- With no partners there is no filter bar, so the row the toolbar
           normally provides has to be kept here. -->
      <div v-else class="flex items-center justify-between gap-16">
        <slot name="prepend" />
        <slot name="actions" />
      </div>
    </div>
    <BittsTablePro
      :columns="columnDefinitions"
      :default-column-def="defaultColumnDefinition"
      :rows="filteredRows"
      :row-height="52"
      row-id="id"
      :pagination="false"
      :suppress-movable-columns="true"
      :no-rows="!filteredRows.length && ready"
      :no-rows-props="noRowsProps"
      :passed-context="gridContext"
      :row-selection="rowSelection"
      class="sharing-settings-by-partners-tab__table"
      @grid-ready="handleGridReady"
    />
  </BittsLoading>
</template>

<script setup lang="ts">
import { type BittsIcon, BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import type { RowSelectionOptions } from '@ag-grid-community/core';
import { computed, ref } from 'vue';

import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import { useTeamStore } from '@/stores';
import { useDataSharesStore } from '@/stores/DataSharesStore';
import { usePartnersStore } from '@/stores/PartnersStore';
import { usePopulationsStore } from '@/stores/PopulationsStore';
import { buildOwnerOptions } from '@/utils/ownerOptions';

import SharingSettingsByPartnersActions from './components/SharingSettingsByPartnersActions.vue';
import {
  defaultColumnDefinition,
  usePartnerTabColumnDefinitions,
} from './composables/usePartnerTabColumnDefinitions';
import usePartnerTabFilters from './composables/usePartnerTabFilters';
import usePartnerTabSelection from './composables/usePartnerTabSelection';
import { buildPartnerRows } from './partnerRows';

/** Hoisted so a re-render cannot hand BittsTablePro a fresh object and churn an AG Grid option. */
const NO_PARTNERS_PROPS = {
  title: 'No partners found',
  description:
    "You don't have any active partners yet. Invite partners to start sharing data.",
  icon: ['fak', 'partner'] as BittsIcon,
};

const NO_MATCHES_PROPS = {
  title: 'No partners match your filters',
  description: 'Try removing a filter or searching for a different partner.',
  icon: ['far', 'search'] as BittsIcon,
};

const partnersStore = usePartnersStore();
const populationsStore = usePopulationsStore();
const dataSharesStore = useDataSharesStore();
const teamStore = useTeamStore();
const { canEdit } = useDataSharingPermissions();

const columnDefinitions = usePartnerTabColumnDefinitions();

const ready = computed(
  () => partnersStore.ready && populationsStore.ready && dataSharesStore.ready,
);

const rows = computed(() => {
  if (!ready.value) return [];

  return buildPartnerRows(partnersStore.partnerOrgs, {
    populations: populationsStore.populations,
    partnerPopulations: populationsStore.partnerPopulations,
    greenfieldSharesLookup: dataSharesStore.greenfieldSharesLookup,
    getOutgoingVisibility: (populationId, partnerOrgId) =>
      dataSharesStore.getEffectiveVisibilityForPopulationWithPartner(
        populationId,
        partnerOrgId,
      ),
    getIncomingSharedFieldCount: (partnerPopulationId) =>
      dataSharesStore.getIncomingSharingRules({
        partnerPopulationId,
        isOverride: false,
      })[0]?.shared_fields.length ?? 0,
  });
});

/**
 * Owner options are identical for every row, so they are built once here rather
 * than per cell instance.
 */
const teamOwnerOptions = computed(() => buildOwnerOptions(teamStore.coreUsers));

const gridContext = {
  canSelectRows: () => canEdit.value,
  teamOwnerOptions: () => teamOwnerOptions.value,
};

const rowSelection = computed<RowSelectionOptions | undefined>(() =>
  canEdit.value
    ? {
        mode: 'multiRow',
        enableClickSelection: false,
        checkboxes: false,
        headerCheckbox: false,
      }
    : undefined,
);

const { filteredRows, applySearch, applyFilters } = usePartnerTabFilters(rows);

const { selectedRows, handleGridReady } = usePartnerTabSelection(filteredRows);

/**
 * Built from the rows the table shows, so the dropdown can never offer a
 * partner that buildPartnerRows has dropped.
 */
const partnerOptions = computed(() =>
  rows.value
    .map((row) => ({ label: row.partner.name, value: row.id }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

const tagOptions = computed(() =>
  partnersStore.partnerTags
    .filter((tag) => tag.label)
    .map((tag) => ({ label: tag.label, value: String(tag.id) }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

const noRowsProps = computed(() =>
  rows.value.length ? NO_MATCHES_PROPS : NO_PARTNERS_PROPS,
);

const hasAnyChanges = ref(false);
const isSaving = ref(false);

defineExpose({
  hasAnyChanges,
  isSaving,
  selectedRows,
  async saveChanges() {
    // No-op: By Partners tab has no editable state yet
  },
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.sharing-settings-by-partners-tab {
  @apply flex flex-col w-full;
}

/* min-h-0 so the grid can shrink inside the column rather than overflow it. */
.sharing-settings-by-partners-tab__table {
  @apply flex-1 min-h-0;
}

.sharing-settings-by-partners-tab__actions {
  @apply px-24 pb-12;
}

/* --- Edge-to-edge table (like PartnerTrackerPage) --- */
:deep(.ag-root-wrapper) {
  @apply rounded-none! border-x-0 border-b-0;
}

/* --- AG Grid column group borders (mirroring PartnerLeaderboardTable) --- */
:deep(.ag-header-cell) {
  @apply border-0!;
}
:deep(.ag-header-group-cell:not(.ag-header-group-cell-no-group)) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.ag-pinned-left-header .ag-header-group-cell-no-group) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.border-end) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.ag-header-row.ag-header-row-column-group) {
  @apply bg-neutral-background text-xs text-neutral-text-strong font-normal;
}

:deep(.ag-header-group-cell-label) {
  @apply justify-center;
}

:deep(.segment-header .ag-header-cell-label) {
  @apply justify-center;
}

:deep(.ag-header-group-cell.ag-header-group-cell-no-group) {
  @apply bg-neutral-background;
}

:deep(.ag-cell-wrapper) {
  @apply h-full min-w-0;
}

:deep(.ag-row-selected::before) {
  @apply bg-transparent;
}

:deep(.ag-overlay-no-rows-wrapper .c-bitts-empty-state-large-border) {
  @apply border-none;
}
</style>
